import {
  Configuration,
  GeoJson,
  ShapesApi,
  ShapesListGet200Response,
} from '@ais-ui/api-spec/shapes';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import { getAuthHeaderFromCredentials, isPresent } from '@ais-ui/utils';
import { Settings, useSaveSettings, useSession } from '@ais-ui/websockets';
import { useQueryClient } from '@tanstack/react-query';
import {
  useTrackedQuery as useQuery,
  useTrackedMutation as useMutation,
  trackOperation,
} from '@ais-ui/performance-tracking';

import { useCallback, useMemo, useState } from 'react';
import { getBasePath } from './getAISBasePath';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useApiClientWrapper } from './apiClientWrapper';
import { ShipApi } from '@ais-ui/api-spec/portserver';

export type CreateShapeResponse = {
  id: string;
};

export type EditShapeResponse = {
  id: string;
};

export type EditCreateShapeResponse = CreateShapeResponse | EditShapeResponse;

const useShapesApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}/aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const shapesApi = useMemo(() => {
    return new ShapesApi(configuration);
  }, [configuration]);
  return {
    shapesApi: apiClientWrapper(shapesApi) as ShapesApi,
    configuration,
  };
};

export const useShapesList = <T = ShapesListGet200Response>(options?: {
  select: (data: ShapesListGet200Response) => T;
}) => {
  const { shapesApi } = useShapesApi();
  return useQuery(
    ['shapes'],
    async () => {
      return await shapesApi.shapesListGet();
    },
    {
      select: options?.select,
      staleTime: 30000, // Consider data fresh for 30 seconds
      refetchInterval: false,
      refetchOnWindowFocus: false,
    }
  );
};

export const useShape = (shapeId: string) => {
  const { shapesApi } = useShapesApi();
  return useQuery(['shape', shapeId], async () => {
    return await shapesApi.shapesIdGet({ id: shapeId });
  });
};

export const useShapeFetch = () => {
  const { shapesApi } = useShapesApi();
  const queryClient = useQueryClient();

  const fetchShape = useCallback(
    async (shapeId: string) => {
      try {
        const cachedData = queryClient.getQueryData(['shape', shapeId]);
        if (cachedData) {
          return cachedData as GeoJson;
        }
        const data = await shapesApi.shapesIdGet({ id: shapeId });
        queryClient.setQueryData(['shape', shapeId], data);
        return data;
      } catch (error) {
        console.error('Error fetching shape:', error);
        throw error;
      }
    },
    [shapesApi, queryClient]
  );

  const trackedFetchShape = (shapeId: string) => {
    return trackOperation(
      () => fetchShape(shapeId),
      `shape.get`,
      'data.fetch',
      {
        resourceId: shapeId,
        resourceType: 'shape',
        timestamp: new Date().toISOString(),
        endpoint: `shapes/${shapeId}`,
      }
    )();
  };

  return trackedFetchShape;
};

export const useFlattenedGeometryShapes = () => {
  const { data, ...rest } = useShapesList<GeoJson[]>({
    select: (response) =>
      response.records ? extractFeatures(response.records) : [],
  });

  return { data, ...rest };
};

function extractFeatures(data: GeoJson[]): GeoJson[] {
  const flatList: GeoJson[] = [];

  // Recursive function to traverse and extract features
  function traverse(features: GeoJson[]) {
    features.forEach((feature) => {
      if (feature.type === 'FeatureCollection') {
        // Recursively traverse nested FeatureCollection
        traverse(feature.features ? feature.features : []);
      } else if (feature.type === 'Feature') {
        flatList.push(feature);
      }
    });
  }

  traverse(data);

  return flatList;
}

export const useShapeGroups = () => {
  return useShapesList({
    select: (data) =>
      data.records != null
        ? data.records.map(groupShapes).filter(isPresent)
        : [],
  });
};

const groupShapes = (shape: GeoJson): GeoJson | null => {
  if (shape.type !== 'FeatureCollection') {
    return null;
  }

  return {
    ...shape,
    features: shape.features?.map(groupShapes).filter(isPresent),
  };
};

export const useShapesInfo = (id: string | undefined | null) => {
  const { shapesApi } = useShapesApi();
  return useQuery(
    ['shapes', { id }],
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return await shapesApi.shapesIdGet({ id: id! });
    },
    { enabled: id != null }
  );
};

export const useShapesCreate = () => {
  const { shapesApi } = useShapesApi();
  const queryClient = useQueryClient();

  return useMutation(
    (input: GeoJson) => {
      return shapesApi.shapesPost({
        json: input,
      }) as unknown as Promise<CreateShapeResponse>;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shapes']);
      },
    }
  );
};

export const useShapesEdit = () => {
  const { shapesApi } = useShapesApi();
  const queryClient = useQueryClient();

  return useMutation(
    (input: { id: string; json: GeoJson }) => {
      return shapesApi.shapesIdPut(
        input
      ) as unknown as Promise<EditShapeResponse>;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shapes']);
      },
    }
  );
};

export const useShapesDelete = (settings: Settings) => {
  const { shapesApi } = useShapesApi();
  const queryClient = useQueryClient();
  const { mutate: mutateSettings } = useSaveSettings();

  return useMutation(
    async (input: { id: string }) => {
      const favoriteShapes =
        settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || [];

      // Step 1: Check if the shape ID exists in the favorite shapes
      if (favoriteShapes.includes(input.id)) {
        const updatedShapes = favoriteShapes.filter(
          (id: string) => id !== input.id
        );

        // Step 2: Prepare settings to save if the shape ID is in favorite_shapes
        const settingsToSave = {
          ...settings,
          'ribbonPlugin.settings': {
            ...settings['ribbonPlugin.settings'],
            favorite_shapes: {
              ...settings['ribbonPlugin.settings']?.favorite_shapes,
              shapes: updatedShapes,
            },
          },
        };

        // Step 3: Save the updated settings
        return new Promise((resolve, reject) => {
          mutateSettings(settingsToSave, {
            onSuccess: () => {
              // Step 4: After settings are successfully saved, delete the shape
              shapesApi.shapesIdDelete(input).then(resolve).catch(reject);
            },
            onError: reject,
          });
        });
      } else {
        // If the shape ID is not in favorite_shapes, proceed to delete the shape without updating settings
        return shapesApi.shapesIdDelete(input);
      }
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['shapes']),
    }
  );
};

export const useShapesImport = () => {
  const { messages } = useLocalization();
  const { shapesApi, configuration } = useShapesApi();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation(
    ({
      name,
      importType,
      importData,
      parentId,
    }: {
      name: string;
      importType: string;
      importData: string;
      parentId: string;
    }) => {
      const payload: any = {
        name,
      };
      const configHeaders = configuration.headers || {};
      let contentType: string;
      if (importType === 'shapefile') {
        const blobBody = new Blob([importData], { type: 'text/plain' });
        payload.type = 'unknown';
        payload.body = blobBody;
        contentType = 'application/octet-stream';
      } else if (importType === 'wkt') {
        const blobBody = new Blob([importData], { type: 'text/plain' });
        payload.type = 'unknown';
        payload.body = blobBody;
        contentType = 'text/plain';
      } else if (importType === 'geojson') {
        try {
          payload.type = 'unknown';
          payload.body = JSON.parse(importData);
          contentType = 'application/json';
        } catch (error) {
          toast({
            title: messages.genericToastTitleError,
            description: 'Invalid GeoJSON data',
            variant: 'destructive',
          });
          throw error;
        }
      } else if (importType === 'kml') {
        try {
          // Validate that it's valid XML by attempting to parse it
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(importData, 'application/xml');
          const parseError = xmlDoc.getElementsByTagName('parsererror');

          if (parseError.length > 0) {
            throw new Error('Invalid XML format');
          }

          const blobBody = new Blob([importData], { type: 'application/xml' });
          payload.type = 'unknown';
          payload.body = blobBody;
          contentType = 'application/xml';
        } catch (error) {
          toast({
            title: messages.genericToastTitleError,
            description: 'Invalid KML data',
            variant: 'destructive',
          });
          throw error;
        }
      } else {
        throw new Error('Unsupported import type');
      }

      if (parentId) {
        payload.parent = parentId;
      }

      const headers = {
        ...configHeaders,
        'Content-Type': contentType,
      };
      return shapesApi.shapesImportPost(payload, { headers });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['shapes']),
    }
  );
};

export const useShapeExport = () => {
  const { shapesApi } = useShapesApi();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // The original exportShape function to be tracked
  const performExport = useCallback(
    async (id: string, format: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await shapesApi.shapesExportIdGet({ id, format });
        setData(response);
        setIsLoading(false);
        return response;
      } catch (err) {
        setError(err as Error);
        setIsLoading(false);
        throw err;
      }
    },
    [shapesApi]
  );

  const exportShape = useCallback(
    (id: string, format: string) => {
      return trackOperation(
        () => performExport(id, format),
        `shape.export`,
        'data.export',
        {
          resourceId: id,
          resourceType: 'shape',
          format,
          endpoint: `shapes/export/${id}`,
        }
      )();
    },
    [performExport]
  );

  return { exportShape, data, error, isLoading };
};
